//src/app/features/taxEstimates/index.tsx
import { useGetClientProfile } from 'admin/features/clients/viewClientDetails/accountInfoClientTab';
import {
  useDeleteTaxEstimateMutation,
  useLazyGetTaxEstimatesQuery,
} from 'app/services/taxEstimates';
import { AddIcon, EditIcon, TrashCanSolidIcon } from 'assets/icons';
import Button from 'Components/Buttons/Button';
import { USDate } from 'Components/Dates/USDate';
import { Filters, FilterSection, RadioGroup } from 'Components/Filters';
import { Grid } from 'Components/Grid';
import usePagination from 'Components/Pagination/usePagination';
import { useConfirmDeleteModal } from 'Components/Popups/ConfirmDeletePopup';
import { ClientBusinessTaskTaxReturnSelect } from 'Components/Selectors/ClientBusinessTaskTaxReturnSelect';
import { Link, useNavigate } from 'react-router-dom';

export function TaxEstimates() {
  const { client, isClient, clientId } = useGetClientProfile();
  const { data, Pagination, filters, setFilters, refetchAfterDelete } =
    usePagination(useLazyGetTaxEstimatesQuery, {
      filters: {
        client_id: client?.id,
      },
      per_page: 10,
    });

  const [DeleteTaxEstimate] = useDeleteTaxEstimateMutation();
  const navigate = useNavigate();
  const confirmDelete = useConfirmDeleteModal();

  return (
    <>
      {/* ───────────────────── Mobile “Add new” button ───────────────────── */}
      {isClient && (
        <div className="mb-4 md:hidden">
          <Link to="create">
            <Button type="button">
              <AddIcon />
              Add new
            </Button>
          </Link>
        </div>
      )}
      <Grid
        footer={<Pagination />}
        headerClassName="border-b border-gray-100 text-gray-dark bg-white"
        isLoading={!data}
        columns={[
          { render: 'Tax Estimates', width: '2fr' },
          'Type',
          'Year',
          'Status',
          'date',
          'Fees',
          {
            render: (
              <div className="flex gap-2 items-center">
                <Filters
                  isLoading={!data}
                  value={filters}
                  onConfirm={setFilters}
                  preExpanded={['status']}
                  locked={client ? ['client_id'] : undefined}
                >
                  {({ updateField, state }) => (
                    <>
                      {
                        <FilterSection>
                          <div className="w-full">
                            <>
                              <ClientBusinessTaskTaxReturnSelect
                                clientId={
                                  client ? client?.id : state?.client_id
                                }
                                businessId={state.business_id}
                                lockClient={client}
                                withProjectSelector={false}
                                withTaskSelector={false}
                                onChange={({ clientId, businessId }) => {
                                  updateField('client_id', clientId);
                                  updateField('business_id', businessId);
                                }}
                              />
                            </>
                          </div>
                        </FilterSection>
                      }

                      <RadioGroup
                        label="Status"
                        name="status"
                        checked={state.status}
                        options={[
                          { value: 'submitted', label: 'Submitted' },
                          { value: 'in_progress', label: 'In Progress' },
                          { value: 'completed', label: 'Completed' },
                          { value: 'cancelled', label: 'Cancelled' },
                        ]}
                        onUpdateField={updateField}
                      />
                    </>
                  )}
                </Filters>
                {/* Desktop / tablet “Add new” button */}
                {isClient && (
                  <Link to="create" className="hidden md:inline-flex">
                    <Button type="button">
                      <AddIcon />
                      Add new
                    </Button>
                  </Link>
                )}
              </div>
            ),
            width: isClient ? '150px' : '100px',
          },
        ]}
        data={data}
        onRowClick={({ data }) => navigate(data.id.toString())}
        renderCells={(te) => [
          te.title,
          te.type,
          te.year,
          te.status.replace('_', ' '),
          <USDate date={te.created_at} />,
          te.estimated_fees_cents && (
            <span className="text-green font-semibold">
              ${te.estimated_fees_cents}
            </span>
          ),
          isClient && te.status === 'submitted' && (
            <div
              className="flex gap-4 items-center max-w-min"
              onClick={(e) => e.stopPropagation()}
            >
              <Button onClick={() => navigate(`${te.id}/edit`)}>
                <EditIcon className="w-4 h-4" />
              </Button>
              <Button
                theme="red-outline"
                onClick={() =>
                  confirmDelete('Tax Estimate', () =>
                    DeleteTaxEstimate(te.id).unwrap().then(refetchAfterDelete),
                  )
                }
              >
                <TrashCanSolidIcon className="w-4 h-4 text-red-200" />
              </Button>
            </div>
          ),
        ]}
      />
    </>
  );
}
